<template>
  <div class="ph-wrap">
    <div class="ph-card">
      <div
          class="ph-header"
          @click="toggleMain"
      >
        <div class="ph-header-left">
          <feather-icon icon="ShieldIcon" size="16" class="text-secondary" />
          <div>
            <div class="ph-header-title">{{ $t('What can each profile do?') }}</div>
            <div class="ph-header-sub">{{ $t('Click to expand the permissions guide') }}</div>
          </div>
        </div>
        <feather-icon
            icon="ChevronDownIcon"
            size="16"
            class="ph-chevron"
            :class="{ 'open': isMainOpen }"
        />
      </div>

      <b-collapse v-model="isMainOpen" class="ph-body-collapse">
        <div class="ph-body open">
          <b-form-input
              v-model="searchQuery"
              type="search"
              :placeholder="$t('Filter profiles...')"
              class="ph-search"
              debounce="300"
          />

          <div class="ph-profiles">
            <div
                v-for="profile in filteredProfiles"
                :key="profile.id"
                class="ph-profile"
                :class="{ 'ph-profile--expanded': expandedProfile === profile.id }"
            >
              <button
                  type="button"
                  class="ph-profile-header"
                  :aria-expanded="expandedProfile === profile.id"
                  @click="toggleProfile(profile.id)"
              >
                <span
                    class="ph-badge"
                    :style="{
                      background: profile.badgeBg,
                      color: profile.badgeColor,
                      borderColor: profile.badgeBorder,
                    }"
                >
                  <feather-icon icon="UserIcon" size="12" class="ph-badge-icon" />
                  {{ profile.label }}
                </span>

                <div class="ph-profile-meta">
                  <span class="ph-profile-desc">{{ profile.desc }}</span>
                  <span class="ph-profile-action">
                    <template v-if="expandedProfile === profile.id">
                      {{ $t('Hide details') }}
                    </template>
                    <template v-else>
                      {{ $t('Click to see details') }}
                    </template>
                    <feather-icon
                        icon="ChevronDownIcon"
                        size="14"
                        class="ph-profile-chevron"
                        :class="{ 'open': expandedProfile === profile.id }"
                    />
                  </span>
                </div>
              </button>

              <b-collapse :visible="expandedProfile === profile.id">
                <div class="ph-profile-body open">
                  <div class="ph-details-label">
                    <feather-icon icon="ListIcon" size="13" />
                    {{ $t('Profile permissions detail') }}
                  </div>
                  <div
                      v-for="(section, index) in profile.sections"
                      :key="index"
                  >
                    <div class="ph-section">
                      <div class="ph-section-title">{{ section.title }}</div>
                      <div class="ph-tags">
                        <span
                            v-for="(tag, tagIndex) in section.tags"
                            :key="tagIndex"
                            class="ph-tag"
                            :class="tag.type"
                        >
                          {{ tag.label }}
                        </span>
                      </div>
                    </div>
                    <div v-if="index < profile.sections.length - 1" class="ph-divider"></div>
                  </div>

                  <div v-if="profile.note" class="ph-note">
                    {{ profile.note }}
                  </div>
                </div>
              </b-collapse>
            </div>

            <div v-if="filteredProfiles.length === 0" class="text-center text-muted mt-2 mb-2">
              <small>{{ $t('No profile found.') }}</small>
            </div>
          </div>

          <div class="ph-legend">
            <span class="ph-legend-item">
              <span class="ph-tag on m-0">{{ $t('Green') }}</span> {{ $t('Allowed') }}
            </span>
            <span class="ph-legend-item">
              <span class="ph-tag off m-0">{{ $t('Red') }}</span> {{ $t('Blocked') }}
            </span>
            <span class="ph-legend-item">
              <span class="ph-tag vi m-0">{{ $t('Yellow') }}</span> {{ $t('View only') }}
            </span>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { BCollapse, BFormInput } from 'bootstrap-vue'
import useUserProfileGuide from './useUserProfileGuide'

export default {
  name: 'UserProfileGuide',
  components: {
    BCollapse,
    BFormInput,
  },
  setup() {
    return useUserProfileGuide()
  },
}
</script>

<style scoped>
/* Fallback color variables to ensure it works regardless of the global theme config */
.ph-wrap {
  --ph-bg-primary: #ffffff;
  --ph-bg-secondary: #f8f8f8;
  --ph-bg-tertiary: #f1f1f1;
  --ph-border-primary: #d1d1d1;
  --ph-border-secondary: #e2e2e2;
  --ph-border-tertiary: #ebebeb;
  --ph-text-primary: #333333;
  --ph-text-secondary: #6e6b7b;
  --ph-radius-md: 6px;
  --ph-radius-lg: 8px;

  font-family: inherit;
  padding: 0.5rem 0;
}

.dark-layout .ph-wrap {
  --ph-bg-primary: #283046;
  --ph-bg-secondary: #161d31;
  --ph-bg-tertiary: #343d55;
  --ph-border-primary: #404656;
  --ph-border-secondary: #3b4253;
  --ph-border-tertiary: #3b4253;
  --ph-text-primary: #d0d2d6;
  --ph-text-secondary: #b4b7bd;
}

.ph-card {
  background: var(--ph-bg-primary);
  border: 1px solid var(--ph-border-tertiary);
  border-radius: var(--ph-radius-lg);
  overflow: hidden;
}

.ph-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  user-select: none;
  background: var(--ph-bg-secondary);
}

.ph-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ph-header-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--ph-text-primary);
}

.ph-header-sub {
  font-size: 12px;
  color: var(--ph-text-secondary);
}

.ph-chevron {
  color: var(--ph-text-secondary);
  transition: transform 0.2s ease-in-out;
}

.ph-chevron.open {
  transform: rotate(180deg);
}

.ph-body-collapse {
  border-top: 1px solid var(--ph-border-tertiary);
}

.ph-body {
  padding: 1rem;
}

.ph-search {
  width: 100%;
  margin-bottom: 0.75rem;
  font-size: 13px;
  background-color: var(--ph-bg-primary);
  color: var(--ph-text-primary);
  border: 1px solid var(--ph-border-secondary);
}

.ph-profiles {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ph-profile {
  border: 1px solid var(--ph-border-tertiary);
  border-radius: var(--ph-radius-md);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.ph-profile--expanded {
  border-color: #7367f0;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.12);
}

.ph-profile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  text-align: left;
  cursor: pointer;
  background: var(--ph-bg-secondary);
  transition: background 0.2s;
}

.ph-profile-header:hover {
  background: var(--ph-bg-tertiary);
}

.ph-profile-header:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(115, 103, 240, 0.35);
}

.ph-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  border-width: 1px;
  border-style: solid;
  line-height: 1.3;
  flex-shrink: 0;
}

.ph-badge-icon {
  flex-shrink: 0;
}

.ph-profile-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.ph-profile-desc {
  font-size: 12px;
  color: var(--ph-text-secondary);
  text-align: right;
}

.ph-profile-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #7367f0;
  white-space: nowrap;
}

.ph-profile-chevron {
  transition: transform 0.2s ease-in-out;
}

.ph-profile-chevron.open {
  transform: rotate(180deg);
}

.ph-profile-body {
  padding: 12px;
  background: var(--ph-bg-primary);
  border-top: 1px solid var(--ph-border-tertiary);
}

.ph-details-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ph-text-secondary);
  margin-bottom: 10px;
}

@media (max-width: 576px) {
  .ph-profile-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .ph-profile-meta {
    align-items: flex-start;
    width: 100%;
  }

  .ph-profile-desc {
    text-align: left;
  }
}

.ph-section {
  margin-bottom: 10px;
}

.ph-section-title {
  font-size: 11px;
  font-weight: 500;
  color: var(--ph-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.ph-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.ph-tag {
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 20px;
  background: var(--ph-bg-secondary);
  color: var(--ph-text-secondary);
  border: 1px solid var(--ph-border-tertiary);
}

.ph-tag.on { background: #EAF3DE; color: #3B6D11; border-color: #97C459; }
.ph-tag.off { background: #FCEBEB; color: #A32D2D; border-color: #F09595; }
.ph-tag.vi { background: #FAEEDA; color: #854F0B; border-color: #EF9F27; }

.dark-layout .ph-tag.on { background: rgba(59, 109, 17, 0.2); color: #C0DD97; border-color: #639922; }
.dark-layout .ph-tag.off { background: rgba(163, 45, 45, 0.2); color: #F7C1C1; border-color: #E24B4A; }
.dark-layout .ph-tag.vi { background: rgba(133, 79, 11, 0.2); color: #FAC775; border-color: #BA7517; }

.ph-note {
  font-size: 12px;
  color: var(--ph-text-secondary);
  margin-top: 8px;
  padding: 6px 8px;
  background: var(--ph-bg-secondary);
  border-radius: var(--ph-radius-md);
  border-left: 2px solid var(--ph-border-primary);
}

.ph-divider {
  height: 1px;
  background: var(--ph-border-tertiary);
  margin: 10px 0;
}

.ph-legend {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--ph-border-tertiary);
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.ph-legend-item {
  font-size: 12px;
  color: var(--ph-text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
